import { useState, DragEvent, ChangeEvent } from 'react';

import { useTranslation } from 'react-i18next';

import FormTextError from '@/components/formTextError/formTextError';

import styles from './styles.module.css';

type Props = {
  label: string;
  formats: string[];
  maxSize: number;
  displayedMaxSize: number;
  blobUrl?: string;
  isError?: boolean;
  errorMessageInvalid?: string;
  whichFile: string;
  disableInput?: boolean;
  setFile: (whichFile: string, file: File) => void;
  handleFileValidity: (whichFile: string, isValid: boolean) => void;
};

const InputFile = ({
  label,
  formats,
  maxSize,
  displayedMaxSize,
  blobUrl,
  isError,
  errorMessageInvalid,
  whichFile,
  disableInput,
  setFile,
  handleFileValidity,
}: Props) => {
  const { t } = useTranslation();
  const versementData =
    sessionStorage.parcoursVersementData === undefined ? undefined : JSON.parse(sessionStorage.parcoursVersementData);

  const shownExtensions = formats.filter((extension) => extension !== 'JPEG');
  const [errorMessage, setErrorMessage] = useState('');

  const [dragActive, setDragActive] = useState(false);

  const [fileName, setFileName] = useState(versementData === undefined ? '' : versementData[whichFile].name);

  const [fileExtension, setFileExtension] = useState<string>(
    versementData === undefined ? '' : versementData[whichFile].extension
  );
  const [fileSizeSymbol, setFileSizeSymbol] = useState(
    versementData === undefined ? '' : versementData[whichFile].symbol
  );
  const [fileSize, setFileSize] = useState(versementData === undefined ? '' : versementData[whichFile].size);

  const [invalidFile, setInvalidFile] = useState(false);

  const handleDrag = function (e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileSize = (size: number) => {
    if (size < 1000) {
      setFileSize(size.toFixed());
      setFileSizeSymbol('o');
    } else if (size < 1000000) {
      setFileSize((size / 1000).toFixed(2));
      setFileSizeSymbol('Ko');
    } else {
      setFileSize((size / 1000000).toFixed(2));
      setFileSizeSymbol('Mo');
    }
  };

  const mafonction = (file: File) => {
    const extensionTmp = file.name.split('.').pop();
    if (extensionTmp) setFileExtension(extensionTmp.toUpperCase());

    handleFileSize(file.size);

    if (extensionTmp && !formats.includes(extensionTmp.toUpperCase()) && extensionTmp.toUpperCase() !== '') {
      handleFileValidity(whichFile, false);
      setInvalidFile(true);
      setErrorMessage('Les formats de fichiers autorisés sont: JPG, PDF, TIFF, GIF');
    } else if (file.size >= 1000000 && Math.floor(file.size / 1000000) > maxSize - 1) {
      handleFileValidity(whichFile, false);
      setInvalidFile(true);
      setErrorMessage(t('inputFile.maxSizeError'));
    } else {
      handleFileValidity(whichFile, true);
      setInvalidFile(false);
    }
  };

  const handleDrop = function (e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && !disableInput) {
      mafonction(e.dataTransfer.files[0]);

      setFileName(e.dataTransfer.files[0].name);

      const extensionTmp = e.dataTransfer.files[0].name.split('.').pop();
      if (extensionTmp) setFileExtension(extensionTmp.toUpperCase());

      handleFileSize(e.dataTransfer.files[0].size);

      setFile(whichFile, e.dataTransfer.files[0]);
    }
  };

  const handleChange = function (e: ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (e.target.files?.[0]) {
      mafonction(e.target.files[0]);

      setFileName(e.target.files[0].name);

      const extensionTmp = e.target.files[0].name.split('.').pop();
      if (extensionTmp) setFileExtension(extensionTmp.toUpperCase());

      handleFileSize(e.target.files[0].size);

      setFile(whichFile, e.target.files[0]);

      e.target.value = '';
    }
  };

  const handleClickTrashcan = () => {
    setFile(whichFile, new File([''], ''));
    setFileName('');

    setFileExtension('');
    setFileSizeSymbol('');
    setFileSize('');

    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...versementData,
        [whichFile]: {
          ...versementData[whichFile],
          base64: '',
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
      })
    );
  };

  let errorText = '';
  if (invalidFile || isError) {
    if (isError) {
      errorText = errorMessageInvalid || '';
    } else {
      errorText = errorMessage;
    }
  }

  const boxClassName = disableInput
    ? `${styles.formFileUploadBox} ${styles.formFileUploadBoxDisabled}`
    : styles.formFileUploadBox;

  const labelClassName = dragActive ? `${styles.labelFileUpload} ${styles.dragActive}` : styles.labelFileUpload;

  const fileBoxClassName =
    invalidFile || isError ? `${styles.formFileUploadFile} ${styles.formFileUploadError}` : styles.formFileUploadFile;

  return (
    <div className={boxClassName}>
      <span className={styles.formFileUploadLabel}>{label}</span>

      <div className={styles.formFileUpload} onSubmit={(e) => e.preventDefault()}>
        <label
          aria-label={t('inputFile.label', { label: label })}
          htmlFor="input-file-upload"
          className={labelClassName}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            className={styles.inputFileUpload}
            multiple={false}
            onChange={handleChange}
            disabled={disableInput}
          />
          <div className={styles.formFileUploadContent}>
            <p className={styles.formFileUploadLegende}>Glissez et déposez votre fichier ici</p>
            <div className={styles.fileUploadIconBox}>
              <i className="totem-icon totem-icon-upload" />
            </div>

            <button className={styles.uploadButton}>
              <span className={styles.uploadButtonTxtOu}>ou</span>{' '}
              <span className={styles.uploadButtonTxt}>ajoutez un ficher</span>
            </button>
          </div>
        </label>

        {dragActive && (
          <div
            aria-hidden="true"
            id="drag-file-element"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          ></div>
        )}
      </div>

      <div className={styles.formFileUploadHelperText}>
        <span>Formats : {shownExtensions.join(', ')}</span>
        <span>Taille max : {displayedMaxSize} Mo</span>
      </div>

      {fileName && (
        <div className={styles.formFileUploadFileBox}>
          <div className={fileBoxClassName}>
            <button
              className={styles.formFileUploadFileLeft}
              onClick={() => window.open(blobUrl, '_blank', 'noopener, noreferrer')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.open(blobUrl, '_blank', 'noopener, noreferrer');
                }
              }}
              tabIndex={0}
              aria-label={`Ouvrir le fichier ${fileName} dans un nouvel onglet`}
            >
              <div className={styles.formFileUploadFileIcon}>
                <i className="totem-icon totem-icon-document" />
                <span>{fileExtension}</span>
              </div>

              <div className={styles.formFileUploadFileInfos}>
                <span className={styles.formFileUploadFileName}>{fileName}</span>
                <span
                  className={`${styles.formFileUploadFileSize}${
                    invalidFile || isError ? ` ${styles.formFileUploadFileSizeError}` : ''
                  }`}
                >
                  {fileSize} {fileSizeSymbol}
                </span>
              </div>
            </button>

            <button
              className={styles.formFileUploadFileDeleteBtn}
              onClick={() => handleClickTrashcan()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClickTrashcan();
                }
              }}
              tabIndex={0}
              aria-label={`Supprimer le fichier ${fileName}`}
            >
              <i className="totem-icon totem-icon-trash" />
            </button>
          </div>

          {(invalidFile || isError) && (
            <div className={styles.formFileTextErrorBox}>
              <FormTextError text={errorText} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InputFile;
